import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { PlanActionButton, PlanCell, fmtDate } from "./AdminUsersTab";
import PlanHistory from "./PlanHistory";
import UserEmailLog from "./UserEmailLog";
import AdminEmailDialog from "./AdminEmailDialog";
import DeleteUserDialog from "./DeleteUserDialog";

function Section({ title, action, children, testId }) {
  return (
    <section className="border-t border-slate-800 pt-4 mt-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="label-tech text-slate-400">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function AdminUserSheet({ target, onClose, onSetTier, onDeleted, t }) {
  const { i18n } = useTranslation();
  const [reloadKey, setReloadKey] = useState(0);
  const [emailOpen, setEmailOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const bump = () => setReloadKey((k) => k + 1);
  const planKey = `${target?.plan?.tier}-${target?.plan?.premium_source}`;

  return (
    <Sheet open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      {target && (
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto bg-slate-950 border-slate-800 text-slate-100" data-testid="admin-user-sheet">
          <SheetHeader>
            <SheetTitle className="text-slate-100 font-mono-data text-base break-all" data-testid="admin-user-sheet-email">{target.email}</SheetTitle>
            <SheetDescription className="text-slate-400 text-xs" data-testid="admin-user-sheet-meta">
              {[...new Set([target.name, target.company_name].filter(Boolean))].join(" · ")}
              {target.created_at ? ` · ${t("admin.registeredOn", { date: fmtDate(target.created_at, i18n.language) })}` : ""}
              {` · ${t("admin.thQuotes")}: ${target.quotes_count ?? 0}`}
            </SheetDescription>
          </SheetHeader>

          <Section title={t("admin.planSection")} testId="admin-user-plan-section"
            action={<PlanActionButton u={target} onSetTier={onSetTier} t={t} />}>
            <PlanCell uid={target.id} plan={target.plan} t={t} />
            <div className="label-tech text-slate-500 mt-4 mb-2">{t("admin.planHistory")}</div>
            <PlanHistory uid={target.id} reloadKey={`${reloadKey}-${planKey}`} t={t} />
          </Section>

          <Section title={t("admin.emailSection")} testId="admin-user-email-section"
            action={<button onClick={() => setEmailOpen(true)} className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="admin-send-email-btn"><Mail size={12} /> {t("admin.sendEmail")}</button>}>
            {target.group_email_opt_out && (
              <div className="text-[11px] text-amber-300/90 mb-3" data-testid="admin-user-group-optout">{t("admin.groupOptOut")}</div>
            )}
            <UserEmailLog uid={target.id} reloadKey={reloadKey} t={t} />
          </Section>

          <Section title={t("admin.dangerZone")} testId="admin-user-danger-section">
            <button onClick={() => setDeleteOpen(true)} className="px-3 py-2 rounded-md text-xs border border-red-500/50 text-red-300 hover:bg-red-500/10 flex items-center gap-1.5" data-testid="admin-delete-user-btn">
              <Trash2 size={12} /> {t("admin.deleteUser")}
            </button>
          </Section>

          <AdminEmailDialog open={emailOpen} onOpenChange={setEmailOpen} target={target} onSent={bump} t={t} />
          <DeleteUserDialog open={deleteOpen} onOpenChange={setDeleteOpen} target={target} onDeleted={onDeleted} t={t} />
        </SheetContent>
      )}
    </Sheet>
  );
}
